import type { Balance } from '@urbanrats/contracts';
import { createGameBridge, type EventData, type EventName, type GameState } from '@urbanrats/embed-sdk';

/** What the embedded game exposes to the bridge. Local UI only: nothing here moves money. */
export interface EmbedController {
  state(): GameState;
  /** Re-reads the balance from the wallet gateway (a fetch; never a value supplied by the parent). */
  refreshBalance(): Promise<Balance | null>;
  setMuted(m: boolean): void;
  setVolume(v: number): void;
  openHistory(): void;
  closePanels(): void;
}

export interface EmbedHandle {
  readonly parentOrigin: string;
  attach(c: EmbedController | null): void;
  emit<E extends EventName>(event: E, data: EventData<E>): void;
}

const LOBBY_STATE: GameState = { gameId: null, gameRoundId: null, roundState: null, balance: null, muted: true, volume: 1, openPanel: null, sessionExpired: false };

/**
 * Starts the embed bridge when the game runs inside an allowlisted operator page. The parent origin is
 * taken from the launch (`parentOrigin`) and must be in the allowlist and equal the real ancestor origin;
 * otherwise the game runs exactly as top-level, without a bridge (same UI either way).
 */
export function startEmbed(allowed: readonly string[], walletCurrency: string): EmbedHandle | null {
  if (window.parent === window) return null;
  const claimed = new URLSearchParams(location.search).get('parentOrigin');
  const ancestor = (location as Location & { ancestorOrigins?: DOMStringList }).ancestorOrigins?.[0] ?? null;
  if (!claimed || !allowed.includes(claimed) || (ancestor !== null && ancestor !== claimed)) {
    console.warn('UrbanRats: embed bridge disabled (parent origin not allowlisted or mismatched)');
    return null;
  }
  let ctl: EmbedController | null = null;
  const state = () => ctl?.state() ?? LOBBY_STATE;
  const bridge = createGameBridge({
    self: window,
    parent: window.parent,
    parentOrigin: claimed,
    handlers: {
      'refresh-balance': async () => {
        const b = (await ctl?.refreshBalance()) ?? null;
        return { ...state(), balance: b?.balance ?? state().balance };
      },
      mute: ({ muted }) => (ctl?.setMuted(muted), state()),
      volume: ({ volume }) => (ctl?.setVolume(volume), state()),
      'open-history': () => (ctl?.openHistory(), state()),
      'close-panels': () => (ctl?.closePanels(), state()),
      'get-state': () => state(),
    },
  });
  bridge.emit('ready', { gameId: null, walletCurrency, embedVersion: 1 });
  return {
    parentOrigin: claimed,
    attach: (c) => {
      ctl = c;
    },
    emit: (e, d) => bridge.emit(e, d),
  };
}
